import React, { useState, useEffect } from 'react';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Typography,
  Chip,
  Grid,
  MenuItem,
  IconButton,
} from '@mui/material';
import {
  Add as AddIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { API } from '../services/api';
import { showSuccess, showError } from '../utils/notifications';
import DataTable from '../components/DataTable';
import FilterBar from '../components/FilterBar';
import ConfirmDialog from '../components/ConfirmDialog';
import ActionButton from '../components/ActionButton';
import TableSkeleton from '../components/TableSkeleton';

const MotionBox = motion(Box);

function Alerts() {
  const [alerts, setAlerts] = useState([]);
  const [devices, setDevices] = useState([]);
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, alertId: null });
  const [editingAlert, setEditingAlert] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [levelFilter, setLevelFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [formData, setFormData] = useState({
    resolution_notes: '',
    status: 1,
  });

  const fetchData = async () => {
    try {
      setLoading(true);
      const [alertsRes, devicesRes, modelsRes] = await Promise.all([
        API.alertHistory.list(),
        API.iotDevices.list(),
        API.models.list(),
      ]);
      setAlerts(alertsRes?.data || []);
      setDevices(devicesRes?.data || []);
      setModels(modelsRes?.data || []);
    } catch (err) {
      showError('Failed to load alerts');
      console.error('Alerts fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleOpenDialog = (alert = null) => {
    if (alert) {
      setEditingAlert(alert);
      setFormData({
        resolution_notes: alert.resolution_notes || '',
        status: alert.status !== undefined ? alert.status : 1,
      });
    } else {
      setEditingAlert(null);
      setFormData({
        resolution_notes: '',
        status: 1,
      });
    }
    setOpen(true);
  };

  const handleCloseDialog = () => {
    setOpen(false);
    setEditingAlert(null);
    setFormData({
      resolution_notes: '',
      status: 1,
    });
  };

  const handleSubmit = async () => {
    try {
      if (editingAlert) {
        await API.alertHistory.update(editingAlert.alert_id, {
          ...formData,
          acknowledged_at: formData.status === 2 ? new Date().toISOString() : editingAlert.acknowledged_at,
          resolved_at: formData.status === 3 ? new Date().toISOString() : editingAlert.resolved_at,
        });
        showSuccess('Alert updated successfully!');
      }
      handleCloseDialog();
      fetchData();
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to update alert';
      showError(errorMessage);
      console.error('Alert submit error:', err);
    }
  };

  const getDeviceName = (deviceId) => {
    const device = devices.find(d => d.device_id === deviceId);
    return device ? device.device_name : 'Unknown';
  };

  const getModelName = (modelId) => {
    const model = models.find(m => m.model_id === modelId);
    return model ? model.model_name : 'Unknown';
  };

  const getAlertLevelColor = (level) => {
    switch (level) {
      case 1: return 'error';
      case 2: return 'error';
      case 3: return 'warning';
      case 4: return 'info';
      default: return 'default';
    }
  };

  const getAlertLevelLabel = (level) => {
    switch (level) {
      case 1: return 'Critical';
      case 2: return 'High';
      case 3: return 'Medium';
      case 4: return 'Low';
      default: return 'Unknown';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 1: return 'error';
      case 2: return 'warning';
      case 3: return 'success';
      default: return 'default';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 1: return 'Active';
      case 2: return 'Acknowledged';
      case 3: return 'Resolved';
      default: return 'Unknown';
    }
  };

  const filteredAlerts = alerts.filter(alert => {
    const matchesSearch = alert.alert_message?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      getDeviceName(alert.device_id)?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      getModelName(alert.model_id)?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesLevel = levelFilter === 'all' || alert.alert_level === parseInt(levelFilter);
    const matchesStatus = statusFilter === 'all' || alert.status === parseInt(statusFilter);
    
    return matchesSearch && matchesLevel && matchesStatus;
  });

  const columns = [
    {
      field: 'alert_message',
      headerName: 'Alert Message',
      width: '25%',
      render: (row) => (
        <Typography variant="body2" fontWeight={600}>
          {row.alert_message}
        </Typography>
      ),
    },
    {
      field: 'device_id',
      headerName: 'Device',
      width: '13%',
      render: (row) => getDeviceName(row.device_id),
    },
    {
      field: 'alert_level',
      headerName: 'Level',
      width: '10%',
      render: (row) => (
        <Chip
          label={getAlertLevelLabel(row.alert_level)}
          color={getAlertLevelColor(row.alert_level)}
          size="small"
          sx={{ fontWeight: 600 }}
        />
      ),
    },
    {
      field: 'sensor_value',
      headerName: 'Value',
      width: '10%',
      render: (row) => row.sensor_value || '-',
    },
    {
      field: 'threshold_value',
      headerName: 'Threshold',
      width: '10%',
      render: (row) => row.threshold_value || '-',
    },
    {
      field: 'triggered_at',
      headerName: 'Triggered',
      width: '12%',
      render: (row) => row.triggered_at ? new Date(row.triggered_at).toLocaleString() : '-',
    },
    {
      field: 'status',
      headerName: 'Status',
      width: '12%',
      render: (row) => (
        <Chip
          label={getStatusLabel(row.status)}
          color={getStatusColor(row.status)}
          size="small"
          sx={{ fontWeight: 600 }}
        />
      ),
    },
  ];

  const filterFields = [
    {
      label: 'Alert Level',
      value: levelFilter,
      onChange: setLevelFilter,
      options: [
        { value: 'all', label: 'All Levels' },
        { value: '1', label: 'Critical' },
        { value: '2', label: 'High' },
        { value: '3', label: 'Medium' },
        { value: '4', label: 'Low' },
      ],
    },
    {
      label: 'Status',
      value: statusFilter,
      onChange: setStatusFilter,
      options: [
        { value: 'all', label: 'All Status' },
        { value: '1', label: 'Active' },
        { value: '2', label: 'Acknowledged' },
        { value: '3', label: 'Resolved' },
      ],
    },
  ];

  if (loading) {
    return (
      <Box>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h5" component="h1" sx={{ fontWeight: 700 }}>
            Alert Rules
          </Typography>
        </Box>
        <TableSkeleton columns={7} rows={6} />
      </Box>
    );
  }

  return (
    <MotionBox
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h5" component="h1" sx={{ fontWeight: 700, mb: 0.5 }}>
            Alert History
          </Typography>
          <Typography variant="body2" color="text.secondary">
            View and manage IoT device alert history
          </Typography>
        </Box>
      </Box>

      <FilterBar
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder="Search alerts..."
        filterFields={filterFields}
      />

      <DataTable
        columns={columns}
        data={filteredAlerts}
        onEdit={handleOpenDialog}
        getRowId={(row) => row.alert_id}
        emptyMessage="No alerts found"
      />

      <Dialog 
        open={open} 
        onClose={handleCloseDialog} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 4,
          },
        }}
      >
        <DialogTitle sx={{ 
          pb: 1, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
        }}>
          <Typography variant="h6" fontWeight={700}>
            {editingAlert ? 'Acknowledge/Resolve Alert' : 'Alert Details'}
          </Typography>
          <IconButton onClick={handleCloseDialog} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {editingAlert && (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Alert Message"
                  value={editingAlert.alert_message}
                  disabled
                  multiline
                  rows={2}
                  sx={{ '& .MuiInputLabel-root': { backgroundColor: 'white', px: 0.5 } }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Device"
                  value={getDeviceName(editingAlert.device_id)}
                  disabled
                  sx={{ '& .MuiInputLabel-root': { backgroundColor: 'white', px: 0.5 } }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Alert Level"
                  value={getAlertLevelLabel(editingAlert.alert_level)}
                  disabled
                  sx={{ '& .MuiInputLabel-root': { backgroundColor: 'white', px: 0.5 } }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Sensor Value"
                  value={editingAlert.sensor_value || '-'}
                  disabled
                  sx={{ '& .MuiInputLabel-root': { backgroundColor: 'white', px: 0.5 } }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Threshold Value"
                  value={editingAlert.threshold_value || '-'}
                  disabled
                  sx={{ '& .MuiInputLabel-root': { backgroundColor: 'white', px: 0.5 } }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  fullWidth
                  label="Status"
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: parseInt(e.target.value) })}
                  sx={{ '& .MuiInputLabel-root': { backgroundColor: 'white', px: 0.5 } }}
                >
                  <MenuItem value={1}>Active</MenuItem>
                  <MenuItem value={2}>Acknowledged</MenuItem>
                  <MenuItem value={3}>Resolved</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Resolution Notes"
                  value={formData.resolution_notes}
                  onChange={(e) => setFormData({ ...formData, resolution_notes: e.target.value })}
                  multiline
                  rows={3}
                  placeholder="Add notes about the resolution..."
                  sx={{ '& .MuiInputLabel-root': { backgroundColor: 'white', px: 0.5 } }}
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <Box sx={{ px: 3, pb: 3, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <ActionButton variant="outlined" onClick={handleCloseDialog}>
            Cancel
          </ActionButton>
          <ActionButton variant="contained" onClick={handleSubmit}>
            Update Alert
          </ActionButton>
        </Box>
      </Dialog>

      <ConfirmDialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, alertId: null })}
        onConfirm={() => {}}
        title="Alert"
        message="Alert history is read-only"
        confirmText="OK"
        type="info"
      />
    </MotionBox>
  );
}

export default Alerts;